/**
 * Key-binding action names for bar slots and page flips (modular action bars
 * plan, phase 3). One scheme for every bar: `bar<B>_slot<S>` fires slot S on
 * bar B's current page; `bar<B>_page_next`, `bar<B>_page_prev` and
 * `bar<B>_page_<N>` flip bar B's page (unbound by default, D5).
 *
 * Saves from before this scheme name bar 1's slots `ability_slot_1` (slot 1)
 * and `ability_1`…`ability_9` (slots 2–10); {@link legacySlotAction} renames them.
 */
import { MAX_BARS, MAX_PAGES_PER_BAR, SLOTS_PER_PAGE } from './barLayout';

export interface SlotPress {
  bar: number;
  slot: number;
}

export type PageFlip = { bar: number; to: 'next' | 'prev' | number };

export const slotAction = (bar: number, slot: number): string => `bar${bar}_slot${slot}`;

export function pageAction(bar: number, to: PageFlip['to']): string {
  return `bar${bar}_page_${to}`;
}

const inRange = (n: number, max: number) => Number.isInteger(n) && n >= 1 && n <= max;

export function parseSlotAction(action: string): SlotPress | null {
  const match = /^bar(\d+)_slot(\d+)$/.exec(action);
  if (!match) return null;
  const press = { bar: Number(match[1]), slot: Number(match[2]) };
  return inRange(press.bar, MAX_BARS) && inRange(press.slot, SLOTS_PER_PAGE) ? press : null;
}

export function parsePageAction(action: string): PageFlip | null {
  const match = /^bar(\d+)_page_(next|prev|\d+)$/.exec(action);
  if (!match) return null;
  const bar = Number(match[1]);
  const to = match[2] === 'next' || match[2] === 'prev' ? match[2] : Number(match[2]);
  if (!inRange(bar, MAX_BARS) || (typeof to === 'number' && !inRange(to, MAX_PAGES_PER_BAR))) return null;
  return { bar, to };
}

/** Every slot action, bar by bar, slot by slot. */
export const SLOT_ACTIONS: readonly string[] = Array.from({ length: MAX_BARS * SLOTS_PER_PAGE }, (_, i) =>
  slotAction(Math.floor(i / SLOTS_PER_PAGE) + 1, (i % SLOTS_PER_PAGE) + 1));

/** Every page action, bar by bar: next, prev, then page 1..N. */
export const PAGE_ACTIONS: readonly string[] = Array.from({ length: MAX_BARS }, (_, i) => [
  pageAction(i + 1, 'next'),
  pageAction(i + 1, 'prev'),
  ...Array.from({ length: MAX_PAGES_PER_BAR }, (_, page) => pageAction(i + 1, page + 1)),
]).flat();

/** The new name for a pre-phase-3 slot action, or null if `action` is not one. */
export function legacySlotAction(action: string): string | null {
  if (action === 'ability_slot_1') return slotAction(1, 1);
  const match = /^ability_([1-9])$/.exec(action);
  return match ? slotAction(1, Number(match[1]) + 1) : null;
}
